import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import type { Product } from "@/hooks/useProducts";
import { getEffectivePrice } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, Package } from "lucide-react";
import { toast } from "sonner";

interface VariationRow {
  id?: number;
  name: string;
  sku: string;
  attribute_1_values: string;
  regular_price: number | null;
  sale_price: number | null;
  in_stock: boolean;
  stock: number | null;
  isNew?: boolean;
}

interface Props {
  product: Product;
}

const AdminVariationsManager = ({ product }: Props) => {
  const qc = useQueryClient();
  const [variations, setVariations] = useState<VariationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);

  // Fetch existing variations
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      // Try parent_id first, fallback to parent_sku
      let { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("parent_id", product.id)
        .eq("type", "variation");

      if (!error && (!data || data.length === 0) && product.sku) {
        const res = await supabase
          .from("products")
          .select("*")
          .eq("parent_sku", product.sku)
          .eq("type", "variation");
        data = res.data;
        error = res.error;
      }

      if (error) {
        toast.error("Failed to load variations");
      } else {
        setVariations(
          (data ?? []).map((v) => ({
            id: v.id,
            name: v.name,
            sku: v.sku || "",
            attribute_1_values: v.attribute_1_values || "",
            regular_price: v.regular_price,
            sale_price: v.sale_price,
            in_stock: v.in_stock,
            stock: v.stock,
          }))
        );
      }
      setLoading(false);
    };
    fetch();
  }, [product.id, product.sku]);

  const addVariation = () => {
    const attrName = product.attribute_1_name || "Option";
    setVariations((prev) => [
      ...prev,
      {
        name: `${product.name} - New Variation`,
        sku: "",
        attribute_1_values: "",
        regular_price: product.regular_price,
        sale_price: product.sale_price,
        in_stock: true,
        stock: null,
        isNew: true,
      },
    ]);
  };

  const updateVariation = (index: number, field: keyof VariationRow, value: any) => {
    setVariations((prev) =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v))
    );
  };

  const removeVariation = async (index: number) => {
    const variation = variations[index];
    if (variation.id) {
      setDeletingIds((prev) => [...prev, variation.id!]);
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", variation.id);
      setDeletingIds((prev) => prev.filter((id) => id !== variation.id));
      if (error) {
        toast.error("Failed to delete variation");
        return;
      }
      toast.success("Variation deleted");
      qc.invalidateQueries({ queryKey: ["variations"] });
    }
    setVariations((prev) => prev.filter((_, i) => i !== index));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const v of variations) {
        const payload = {
          name: v.name,
          sku: v.sku || null,
          attribute_1_name: product.attribute_1_name || null,
          attribute_1_values: v.attribute_1_values || null,
          regular_price: v.regular_price,
          sale_price: v.sale_price,
          in_stock: v.in_stock,
          stock: v.stock,
          type: "variation" as const,
          parent_id: product.id,
          parent_sku: product.sku || null,
          published: false,
          categories: product.categories,
        };

        if (v.id) {
          const { error } = await supabase
            .from("products")
            .update(payload)
            .eq("id", v.id);
          if (error) throw error;
        } else {
          const { data, error } = await supabase
            .from("products")
            .insert(payload)
            .select()
            .single();
          if (error) throw error;
          v.id = data.id;
          v.isNew = false;
        }
      }

      // Sync parent attribute_1_values from variation values
      const allAttrValues = variations
        .map((v) => v.attribute_1_values)
        .filter(Boolean)
        .join(", ");
      if (allAttrValues && product.attribute_1_name) {
        await supabase
          .from("products")
          .update({ attribute_1_values: allAttrValues })
          .eq("id", product.id);
      }

      toast.success("All variations saved!");
      qc.invalidateQueries({ queryKey: ["variations"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to save variations");
    } finally {
      setSaving(false);
    }
  };

  if (product.type !== "variable") return null;

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground py-4">Loading variations…</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold flex items-center gap-2">
          <Package className="w-4 h-4 text-primary" />
          Variations ({variations.length})
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addVariation} className="gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Add Variation
          </Button>
          {variations.length > 0 && (
            <Button size="sm" onClick={saveAll} disabled={saving} className="gap-1.5">
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving…" : "Save All"}
            </Button>
          )}
        </div>
      </div>

      {variations.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
          No variations yet. Click "Add Variation" to create one.
        </p>
      ) : (
        <div className="space-y-3">
          {variations.map((v, i) => (
            <div
              key={v.id ?? `new-${i}`}
              className={`border rounded-lg p-4 space-y-3 ${
                v.isNew ? "border-primary/40 bg-primary/5" : "border-border bg-card"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Variation Name</Label>
                    <Input
                      value={v.name}
                      onChange={(e) => updateVariation(i, "name", e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">SKU</Label>
                    <Input
                      value={v.sku}
                      onChange={(e) => updateVariation(i, "sku", e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeVariation(i)}
                  disabled={deletingIds.includes(v.id ?? 0)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 mt-5"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">
                    {product.attribute_1_name || "Attribute"} Value
                  </Label>
                  <Input
                    value={v.attribute_1_values}
                    onChange={(e) => updateVariation(i, "attribute_1_values", e.target.value)}
                    placeholder="e.g. Black"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Regular Price (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={v.regular_price ?? ""}
                    onChange={(e) =>
                      updateVariation(i, "regular_price", e.target.value ? Number(e.target.value) : null)
                    }
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Sale Price (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={v.sale_price ?? ""}
                    onChange={(e) =>
                      updateVariation(i, "sale_price", e.target.value ? Number(e.target.value) : null)
                    }
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Stock</Label>
                  <Input
                    type="number"
                    value={v.stock ?? ""}
                    onChange={(e) =>
                      updateVariation(i, "stock", e.target.value ? Number(e.target.value) : null)
                    }
                    className="text-sm"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs font-body">
                <input
                  type="checkbox"
                  checked={v.in_stock}
                  onChange={(e) => updateVariation(i, "in_stock", e.target.checked)}
                  className="rounded border-input"
                />
                In Stock
              </label>

              {v.id && !v.isNew && (
                <p className="text-[10px] text-muted-foreground">
                  Effective price: £{getEffectivePrice({ regular_price: v.regular_price, sale_price: v.sale_price }).toFixed(2)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminVariationsManager;
